import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { CircleCheck, Clock, FileText } from 'lucide-react'
import { useState } from 'react'

import { BarraTitulo } from '@/components/cadastros/base'
import { Button } from '@/components/ui/button'
import { Input, MensagemFormulario } from '@/components/ui/campo'
import { Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { toast } from '@/components/ui/toast'
import { mensagemDeErro } from '@/lib/erros'
import { competenciaAnterior, formatarDataHora, nomeCompetencia, nota, plural } from '@/lib/formato'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/fechamentos/')({ component: Fechamentos })

/**
 * Fechamento mensal (RF-D07): gera o documento de cada operador para um mês
 * encerrado e acompanha quem já deu ciência no tablet.
 */
function Fechamentos() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [competencia, setCompetencia] = useState(competenciaAnterior)
  const [mensagem, setMensagem] = useState<{ erro?: string }>({})
  const lista = useQuery(trpc.avaliacao.fechamentos.listar.queryOptions({ competencia }))
  const gerar = useMutation(
    trpc.avaliacao.fechamentos.gerar.mutationOptions({
      onSuccess: async (r) => {
        toast.sucesso(
          `${plural(r.gerados, 'fechamento gerado', 'fechamentos gerados')} para ${nomeCompetencia(r.competencia)}${
            r.mantidos > 0
              ? `; ${plural(r.mantidos, 'com ciência mantido', 'com ciência mantidos')}`
              : ''
          }.`,
        )
        await qc.invalidateQueries({ queryKey: trpc.avaliacao.fechamentos.listar.queryKey() })
      },
      onError: (e) => setMensagem({ erro: mensagemDeErro(e) }),
    }),
  )
  const comCiencia = lista.data?.filter((f) => f.cienciaEm).length ?? 0

  return (
    <>
      <BarraTitulo
        titulo="Fechamentos mensais"
        descricao="Um documento por operador com o mês inteiro (dias, fichas, pausas, nota), guardado com hash. O operador dá ciência no tablet com o PIN."
      />
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Competência
          <Input
            type="month"
            value={competencia}
            onChange={(e) => {
              setCompetencia(e.target.value || competenciaAnterior())
              setMensagem({})
            }}
            className="w-48"
          />
        </label>
        <Button
          onClick={() => {
            setMensagem({})
            gerar.mutate({ competencia })
          }}
          carregando={gerar.isPending}
        >
          {lista.data?.length ? 'Gerar de novo (sem ciência)' : 'Gerar fechamentos'}
        </Button>
        {lista.data && lista.data.length > 0 && (
          <p className="text-sm text-fg-muted">
            {comCiencia} de {lista.data.length} com ciência
          </p>
        )}
      </div>
      {mensagem.erro && (
        <div className="mb-4">
          <MensagemFormulario erro={mensagem.erro} />
        </div>
      )}

      <Tabela>
        <Cabecalho>
          <Th>Operador</Th>
          <Th className="text-right">Dias</Th>
          <Th className="text-right">Faltas</Th>
          <Th className="text-right">Nota</Th>
          <Th>Faixa</Th>
          <Th>Gerado em</Th>
          <Th>Ciência</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((f) => (
            <Linha key={f.id}>
              <Td className="font-medium">
                {f.operador.nome}
                {!f.operador.ativo && (
                  <span className="ml-2 text-xs text-fg-subtle">desativado</span>
                )}
              </Td>
              <Td numerico className="text-right">
                {f.diasTrabalhados}
              </Td>
              <Td numerico className={`text-right ${f.faltas > 0 ? 'text-vermelho' : ''}`}>
                {f.faltas}
              </Td>
              <Td numerico className="text-right font-bold">
                {nota(f.notaMedia)}
              </Td>
              <Td className="text-fg-muted">{f.faixa ?? '—'}</Td>
              <Td numerico className="text-fg-muted">
                {formatarDataHora(f.geradoEm)}
              </Td>
              <Td>
                {f.cienciaEm ? (
                  <span className="inline-flex items-center gap-1 text-verde">
                    <CircleCheck className="size-4" aria-hidden />
                    {formatarDataHora(f.cienciaEm)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amarelo">
                    <Clock className="size-4" aria-hidden /> pendente
                  </span>
                )}
              </Td>
              <Td className="text-right">
                <Link
                  to="/gestao/fechamentos/$fechamentoId"
                  params={{ fechamentoId: f.id }}
                  className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
                >
                  <FileText className="size-4" aria-hidden /> Abrir
                </Link>
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={8} />}
          {lista.data?.length === 0 && (
            <Vazio colunas={8}>
              Nenhum fechamento para {nomeCompetencia(competencia)}. Gere quando o mês terminar.
            </Vazio>
          )}
        </tbody>
      </Tabela>
    </>
  )
}
